/**
 * Daily 에 붙는 것들의 시드 — 코멘트(`daily_comment`), 사진(`daily_image`), 수정 요청
 * (`daily_edit_request`).
 *
 * 코멘트는 Daily 값과 집계를 바꾸지 않는다. 사진은 파일 바이트를 둘 곳이 없어 mock 은 메타데이터만
 * 들고 있고, 내용 조회는 handler 가 작은 자리표시 이미지를 돌려준다.
 */
import type { DailyComment, DailyEditRequest, DailyImage } from "@/types";
import { dailies } from "./dailies";
import { PM_OF } from "./projects";
import { at, rng } from "./seed";

export interface MockComment extends DailyComment {
  daily_table_id: string;
  deleted_at: string | null;
}

export interface MockImage extends DailyImage {
  daily_table_id: string;
}

const dailyOf = (userId: string, date: string) =>
  dailies.find((row) => row.user_id === userId && row.work_date === date);

const staffDaily = dailyOf("u-staff", "2026-10-21");
const pmDaily = dailyOf("u-pm", "2026-10-20");

const fixedComments: MockComment[] = staffDaily
  ? [
      {
        id: "daily-comment-1",
        daily_table_id: staffDaily.id,
        daily_row_id: staffDaily.rows[0]?.id ?? null,
        author_id: "u-pm",
        body: "이 줄 시간이 좀 많아 보여요. 어떤 작업이었는지 한 줄 더 적어 주세요.",
        created_at: at("2026-10-21", "19:12"),
        updated_at: null,
        deleted_at: null,
      },
      {
        id: "daily-comment-2",
        daily_table_id: staffDaily.id,
        daily_row_id: null,
        author_id: "u-staff",
        body: "창호 상세가 생각보다 오래 걸렸습니다. 다음 줄에 나눠 적겠습니다.",
        created_at: at("2026-10-21", "20:02"),
        updated_at: null,
        deleted_at: null,
      },
    ]
  : [];

const fixedImages: MockImage[] = pmDaily
  ? [
      {
        id: "daily-image-1",
        daily_table_id: pmDaily.id,
        project_id: pmDaily.rows[0]?.project_id ?? null,
        daily_row_id: null,
        file_name: "현장-골조-01.jpg",
        content_type: "image/jpeg",
        byte_size: 482_118,
        position: 1,
        uploaded_by: "u-pm",
        created_at: at("2026-10-20", "18:40"),
      },
    ]
  : [];

/* ── 작업별 코멘트와 사진 — 최근 열흘 남짓 ─────────────────────────────── */

/** PM 이나 관리자가 작업에 남기는 말과, 쓴 사람이 다는 답. */
const REVIEW = [
  "도면 번호도 같이 적어 주세요.",
  "발주처 회신 오면 이 작업에 이어서 남겨 주세요.",
  "이 부분은 내일 회의 전에 한 번 같이 봐요.",
  "시간이 계획보다 많이 들었네요. 막힌 데가 있었나요?",
  "좋습니다. 이대로 발주처에 보내도 될 것 같아요.",
  "구조 쪽 의견도 받아서 반영해 주세요.",
  "사진 몇 장 더 올려 주면 리포트에 쓰기 좋겠어요.",
];
const REPLY = [
  "네, 반영해서 다시 올릴게요.",
  "확인했습니다. 내일 오전에 마무리할게요.",
  "구조팀 회신 기다리는 중이에요.",
  "사진 추가했습니다.",
];
const PHOTO = ["현장", "외관", "조형물", "준공", "모형"];

const pmOf = (projectId: string | null) => (projectId ? PM_OF.get(projectId) : undefined);

/** 시드가 「오늘」로 삼는 날부터 2주 전까지 — 보드에서 눈에 띄는 범위. */
const recentFrom = (() => {
  const last = dailies.reduce((max, d) => (d.work_date > max ? d.work_date : max), "");
  const date = new Date(`${last}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() - 14);
  return date.toISOString().slice(0, 10);
})();

const generated = dailies
  .filter((d) => d.work_date >= recentFrom)
  .flatMap((daily) =>
    daily.rows.map((row) => ({ daily, row, r: rng(`extras:${daily.id}:${row.id}`) }))
  );

let commentSeq = 0;
const reviewComments: MockComment[] = generated.flatMap(({ daily, row, r }) => {
  if (!r.chance(0.18)) return [];
  // 프로젝트 작업은 그 PM 이, 아니면 관리자가 단다. 자기 작업에는 PM 이라도 달지 않는다.
  const reviewer = pmOf(row.project_id) ?? "변경민";
  if (reviewer === daily.user_id) return [];
  const comment = (author: string, body: string, time: string): MockComment => ({
    id: `daily-comment-g${++commentSeq}`,
    daily_table_id: daily.id,
    daily_row_id: row.id,
    author_id: author,
    body,
    created_at: at(daily.work_date, time),
    updated_at: null,
    deleted_at: null,
  });
  const first = comment(reviewer, r.pick(REVIEW), `1${1 + r.int(7)}:${String(10 + r.int(49))}`);
  return r.chance(0.45)
    ? [first, comment(daily.user_id, r.pick(REPLY), `1${8}:${String(10 + r.int(49))}`)]
    : [first];
});

let imageSeq = 0;
const taskImages: MockImage[] = generated.flatMap(({ daily, row, r }) => {
  // 한 일에 붙인 사진 — 끝낸 프로젝트 작업의 절반쯤에, 하루·프로젝트당 상한(3장) 안에서. 사진은
  // 사무소 작업 사진 다섯 장 중에서 보인다(→ dailyPhotos.ts).
  if (!row.project_id || !row.description || !r.chance(0.5)) return [];
  return Array.from({ length: 1 + r.int(3) }, (_, i) => ({
    id: `daily-image-g${++imageSeq}`,
    daily_table_id: daily.id,
    project_id: row.project_id,
    daily_row_id: row.id,
    file_name: `${r.pick(PHOTO)}-${String(i + 1).padStart(2, "0")}.jpg`,
    content_type: "image/jpeg",
    byte_size: 300_000 + r.int(400_000),
    position: i + 1,
    uploaded_by: daily.user_id,
    created_at: at(daily.work_date, "17:30"),
  }));
});

export const dailyComments: MockComment[] = [...fixedComments, ...reviewComments];

/* ── Daily 수정 요청 — 관리자 Daily 화면의 패널과 알림에서 바로 결재해 보도록 대기 중인 것 둘 ─────── */

const REQUESTS: { userId: string; name: string; reason: string; extra: number; note: string }[] = [
  // 최다민 — 알림 notif-6 과 짝
  {
    userId: "u-st4",
    name: "최다민",
    reason: "현장 대응으로 늦게까지 남았는데 시간을 적게 적었어요.",
    extra: 1.5,
    note: "현장 대응",
  },
  {
    userId: "u-st3",
    name: "오세진",
    reason: "도면 번호를 빠뜨려서 한 일에 더 적고 싶어요.",
    extra: 0,
    note: "도면 A-201~205",
  },
];

export const dailyEditRequests: DailyEditRequest[] = REQUESTS.flatMap((req, n) => {
  const target = dailies
    .filter((d) => d.user_id === req.userId && d.status === "submitted" && d.rows.length > 0)
    .sort((a, b) => b.work_date.localeCompare(a.work_date))[0];
  if (!target) return [];
  return [
    {
      id: `edit-request-${n + 1}`,
      daily_table_id: target.id,
      work_date: target.work_date,
      requester_id: req.userId,
      requester_name: req.name,
      reason: req.reason,
      before: target.rows.map((row) => ({
        id: row.id,
        project_id: row.project_id,
        category_id: row.category_id,
        plan_description: row.plan_description,
        plan_hours: row.plan_hours,
        description: row.description,
        actual_hours: row.actual_hours,
        done: !!row.completed_at,
      })),
      after: target.rows.map((row, i) => ({
        id: row.id,
        project_id: row.project_id,
        category_id: row.category_id,
        description: i === 0 ? `${row.description} · ${req.note}` : row.description,
        actual_hours: i === 0 ? row.actual_hours + req.extra : row.actual_hours,
      })),
      notes: null,
      status: "pending",
      decider_id: null,
      decided_at: null,
      decision_note: "",
      applied_at: null,
      created_at: at(target.work_date, `21:${String(10 + n * 7)}`),
    },
  ];
});
export const dailyImages: MockImage[] = [...fixedImages, ...taskImages];
